"use client";

/**
 * The three ways an AI product gets web knowledge, as a diagram that runs itself.
 *
 * THE ARGUMENT IS THE SHAPE OF THE CHAIN, NOT A SCORE. Each row shows the same journey — the web at
 * one end, something useful at the other — and the reader is meant to notice two things without
 * being told: the traditional chain is fully owned but stops at links, and the rented chain reaches
 * an answer but starts inside a box its operator does not control. Ours is the only one that is both
 * owned end to end and ends in an answer.
 *
 * WRITTEN TO SURVIVE A COMPETITOR READING IT. No vendor is called fragile, dependent or doomed. The
 * traditional index is credited with owning its chain, because it does. The rented model is
 * described as fast to launch and genuinely popular, because it is. Every unflattering conclusion is
 * left for the reader to reach from the diagram, which is both more persuasive and not something
 * anyone can demand we retract.
 *
 * DELIBERATELY VAGUE IN ONE PLACE: our own chain says "we read it" and nothing about how. The method
 * is the part worth protecting, and a marketing diagram is the last place it should appear.
 *
 * The loop runs on one clock for all three rows, so the shorter chain visibly finishes first. That
 * is not decoration — fewer hops is the consequence of owning the whole path.
 */

import { useEffect, useState } from "react";
import {
  Globe,
  Database,
  Link2,
  Sparkles,
  BookOpen,
  Receipt,
  type LucideIcon,
} from "lucide-react";

type Step = {
  icon: LucideIcon;
  t: string;
  /** Renders dashed, with a caption: this link in the chain belongs to someone else. */
  external?: boolean;
};

type Model = {
  key: string;
  label: string;
  who: string;
  tone: "slate" | "amber" | "emerald";
  steps: Step[];
  end: string;
  /** What this model cannot do, stated as a consequence rather than an insult. */
  limits: { t: string; b: string }[];
  us?: boolean;
};

const MODELS: Model[] = [
  {
    key: "traditional",
    label: "The traditional index",
    who: "Google, Bing",
    tone: "slate",
    steps: [
      { icon: Globe, t: "The web" },
      { icon: Database, t: "Their index" },
      { icon: Link2, t: "A page of links" },
    ],
    end: "Owned end to end, and built to send you somewhere rather than to answer you. It was designed decades before anything needed to read pages for meaning.",
    limits: [
      { t: "Returns links, not passages", b: "Stores what a page links to rather than what it says, so an answer can point at a homepage but not at the paragraph that settles the question." },
      { t: "Everything is a web page", b: "A judgment, a standard and a clinical trial are filed the same way as a blog post — no jurisdiction, no revision, no trial status to cite." },
      { t: "A snapshot, not a live picture", b: "Crawling runs in cycles, so what you search is the web as it was when the cycle last reached it — days or weeks behind, and the same snapshot for everyone regardless of what anyone is actually asking about." },
      { t: "Refreshed on its own schedule", b: "What gets re-read is decided by the crawler's plan, not by what people are asking today. Interest in a subject does not make it any fresher." },
      { t: "Ranked to sell placement", b: "The order is shaped by an advertising business. That is a fine trade for browsing and a poor one for answering." },
    ],
  },
  {
    key: "rented",
    label: "The rented index",
    who: "Most AI products",
    tone: "amber",
    steps: [
      { icon: Database, t: "Someone else's index", external: true },
      { icon: Receipt, t: "A fee per query" },
      { icon: Sparkles, t: "An answer" },
    ],
    end: "The quickest way to launch, and the reason it is the common choice. The chain starts outside the product, so the rate and the limits are set elsewhere.",
    limits: [
      { t: "The price is not theirs", b: "Every answer carries a per-query fee set by the provider. A rate rise lands on the product's margin, not the provider's." },
      { t: "The ceiling is not theirs either", b: "Rate limits, quotas and terms belong to whoever owns the index. Growth needs their permission as much as demand." },
      { t: "Reaches only what was sold", b: "You get what the upstream index holds. Material it never collected cannot be bought back at any price." },
      { t: "It can end", b: "When one large provider retired its search API in 2025, every product built on it had to move at once. That risk comes with the model." },
    ],
  },
  {
    key: "ours",
    label: "Ours",
    who: "askFinz",
    tone: "emerald",
    steps: [
      { icon: Globe, t: "The web" },
      { icon: BookOpen, t: "We read it" },
      { icon: Database, t: "Our index" },
      { icon: Sparkles, t: "An answer" },
    ],
    end: "Owned end to end and built to answer. Years of reading went into it, and that is the reason the price is ours to set rather than someone else's to raise.",
    limits: [
      { t: "Read continuously, and where it is wanted", b: "Reading never stops and never waits for a cycle, and sources people actually ask about are re-read rather than left until the schedule comes round. What you search is the web now, not the web as it was." },
      { t: "Smaller than the giants", b: "An index decades old is far larger. We compete on what is held, how it is filed and what it costs — not on being the biggest." },
      { t: "Ours to maintain", b: "Storage, machines and quality control are our problem forever. Cheap per page is what makes that an ordinary cost rather than the business itself." },
    ],
    us: true,
  },
];

const TONE = {
  slate: {
    dot: "bg-slate-400",
    ring: "ring-slate-400/40",
    text: "text-slate-600 dark:text-slate-300",
    line: "bg-slate-400",
  },
  amber: {
    dot: "bg-amber-400",
    ring: "ring-amber-400/40",
    text: "text-amber-600 dark:text-amber-400",
    line: "bg-amber-400",
  },
  emerald: {
    dot: "bg-emerald-500",
    ring: "ring-emerald-500/40",
    text: "text-emerald-600 dark:text-emerald-400",
    line: "bg-emerald-500",
  },
} as const;

const LONGEST = Math.max(...MODELS.map((m) => m.steps.length));

export function IndexModelsDiagram() {
  // One clock, all three rows. `tick` counts past the longest chain and then holds for two beats so
  // a finished pass is readable before it resets — without the pause the eye never lands on the
  // completed state, which is the one that carries the comparison.
  const [tick, setTick] = useState(0);
  const [passes, setPasses] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setTick(LONGEST);
      setPasses(1);
      return;
    }

    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % (LONGEST + 2);
      setTick(i);
      // Each completed pass is one more query, which is what makes the running total move.
      if (i === 0) setPasses((p) => Math.min(p + 1, 999));
    }, 900);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl border hairline-strong bg-card">
      <div className="flex items-center justify-between border-b hairline px-6 py-4">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-foreground/50">
          Where the web knowledge comes from
        </span>
        <span className="hidden font-mono text-xs text-foreground/40 sm:inline">
          three models · same journey
        </span>
      </div>

      <div className="divide-y hairline">
        {MODELS.map((m) => {
          const tone = TONE[m.tone];
          return (
            <div
              key={m.key}
              className={`px-6 py-7 md:px-10 ${m.us ? "bg-foreground/[0.04]" : ""}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3
                  className={`font-semibold ${m.us ? "text-foreground" : "text-foreground/85"}`}
                >
                  {m.label}
                </h3>
                <span className="text-[11px] uppercase tracking-[0.14em] text-foreground/45">
                  {m.who}
                </span>
              </div>

              {/* The chain. Wraps on narrow screens rather than scrolling: a diagram a reader has to
                  drag sideways to understand has already failed at its one job. */}
              <div className="mt-5 flex flex-wrap items-center gap-y-3">
                {m.steps.map((s, i) => {
                  const on = tick > i;
                  const Icon = s.icon;
                  return (
                    <div key={s.t} className="flex items-center">
                      <div
                        className={[
                          "flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 transition-all duration-500",
                          s.external ? "border-dashed" : "",
                          on
                            ? `hairline-strong ring-1 ${tone.ring} bg-background`
                            : "hairline opacity-50",
                        ].join(" ")}
                      >
                        <Icon
                          className={[
                            "h-4 w-4 shrink-0 transition-colors duration-500",
                            on ? tone.text : "text-foreground/35",
                          ].join(" ")}
                          aria-hidden
                        />
                        <div className="leading-tight">
                          <span
                            className={[
                              "block whitespace-nowrap text-xs font-medium transition-colors duration-500",
                              on ? "text-foreground/85" : "text-foreground/45",
                            ].join(" ")}
                          >
                            {s.t}
                          </span>
                          {s.external && (
                            <span className="block text-[10px] text-foreground/45">
                              not theirs to set
                            </span>
                          )}
                        </div>
                      </div>

                      {i < m.steps.length - 1 && (
                        <span
                          className="relative mx-1.5 block h-px w-6 overflow-hidden bg-foreground/15 sm:w-9"
                          aria-hidden
                        >
                          <span
                            className={[
                              "absolute inset-y-0 left-0 transition-all duration-500",
                              tone.line,
                              tick > i + 1 ? "w-full opacity-100" : "w-0 opacity-0",
                            ].join(" ")}
                          />
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* The running total sits on the rented row only, because it is the only row where a
                    completed pass costs the operator money. It counts real loops of this animation
                    rather than a made-up figure, and carries no currency — inventing a rate here
                    would be a claim about someone else's pricing that this diagram cannot support. */}
                {m.key === "rented" && (
                  <span className="ml-3 whitespace-nowrap font-mono text-[11px] text-foreground/45">
                    queries billed: {passes}
                  </span>
                )}
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground/65">
                {m.end}
              </p>

              {/* WHAT EACH MODEL COSTS YOU. The diagram alone showed the shape of each chain and left
                  the consequences implicit, which read as simpler than the decision actually is.
                  Ours carries its OWN limits — smaller than the giants, ours to maintain — because a
                  comparison where only the competitors have drawbacks is an advertisement, and a
                  reader can tell. Admitting the two real weaknesses is what makes the rest credible. */}
              <div className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {m.limits.map((l) => (
                  <div key={l.t} className="flex gap-2">
                    <span
                      className={`mt-[7px] h-1 w-1 shrink-0 rounded-full ${m.us ? tone.dot : "bg-foreground/25"}`}
                      aria-hidden
                    />
                    <p className="text-xs leading-relaxed text-foreground/55">
                      <span className={`font-medium ${m.us ? "text-foreground/75" : "text-foreground/70"}`}>
                        {l.t}.
                      </span>{" "}
                      {l.b}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t hairline px-6 py-5 md:px-10">
        <p className="max-w-3xl text-sm leading-relaxed text-foreground/70">
          Two of these chains are owned end to end. Two of them finish with an answer. Only one does
          both — and that is the whole of why our pricing looks the way it does further down this
          page. Ours is not the largest index in the world and we do not claim to be; we compete on
          what is held, how it is filed and what it costs.
        </p>
      </div>
    </div>
  );
}
